/**
 * Row grouping for the Executions live monitor: group the gradual (newest-first)
 * execution list by execution type (flow / connector / ...) or by status, via
 * the shared RecordTable `options.groupBy`.
 *
 * Group order is fixed, so a group header does not jump when a live row arrives:
 * types sort alphabetically by label, statuses follow the lifecycle order
 * (waiting -> running -> paused -> ended). Rows inside a group keep the list's
 * newest-first order.
 * (operator-task-fe-execution-live-monitor-recordtable-grouping)
 */
import type { GroupWithMembers } from "components/Table/groupRows";
import { ExecutionStatus } from "config/ts-utils";
import { statusLabels } from "utils/utils";

export type ExecutionGroupBy = "none" | "type" | "status";

export const EXECUTION_GROUP_BY_OPTIONS: { value: ExecutionGroupBy; label: string }[] = [
    { value: "none", label: "No grouping" },
    { value: "type", label: "Group by type" },
    { value: "status", label: "Group by status" },
];

export const EXECUTION_GROUP_BY_STORAGE_KEY = "executions-listing-group-by";

const STATUS_ORDER: string[] = [
    ExecutionStatus.SCHEDULED,
    ExecutionStatus.WAITING_ENGINE,
    ExecutionStatus.WAITING_DEPENDENCY,
    ExecutionStatus.WAITING_LOCK,
    ExecutionStatus.WAITING_RECORD,
    ExecutionStatus.WAITING_RESPONSE,
    ExecutionStatus.AT_ENGINE,
    ExecutionStatus.PAUSED,
    ExecutionStatus.ENDED_ERROR,
    ExecutionStatus.ENDED_CANCELLED,
    ExecutionStatus.ENDED_SUCCESS,
];

export const parseExecutionGroupBy = (value: unknown): ExecutionGroupBy =>
    value === "type" || value === "status" ? value : "none";

/** "CONNECTOR" -> "Connector", "" -> "(none)". */
export const executionTypeLabel = (type: string): string =>
    type ? type.charAt(0).toUpperCase() + type.slice(1).toLowerCase() : "(none)";

export const executionStatusLabel = (status: string): string =>
    (statusLabels as Record<string, string>)[status] ?? (status || "(none)");

const statusRank = (status: string): number => {
    const index = STATUS_ORDER.indexOf(status);
    return index === -1 ? STATUS_ORDER.length : index;
};

export const sortTypeGroups = <T>(groups: GroupWithMembers<T>[]): GroupWithMembers<T>[] =>
    [...groups].sort((a, b) => a.label.localeCompare(b.label));

export const sortStatusGroups = <T>(groups: GroupWithMembers<T>[]): GroupWithMembers<T>[] =>
    [...groups].sort((a, b) => statusRank(a.key) - statusRank(b.key) || a.key.localeCompare(b.key));

type ExecutionRow = { type?: string | null; status?: string | null };

/** The RecordTable `options.groupBy` config for a mode, or undefined for "none". */
export const executionGroupByConfig = (mode: ExecutionGroupBy) => {
    if (mode === "type") {
        return {
            getGroupKey: (row: ExecutionRow) => String(row?.type ?? ""),
            getGroupLabel: (_row: ExecutionRow, key: string) => executionTypeLabel(key),
            sortGroups: sortTypeGroups,
        };
    }
    if (mode === "status") {
        return {
            getGroupKey: (row: ExecutionRow) => String(row?.status ?? ""),
            getGroupLabel: (_row: ExecutionRow, key: string) => executionStatusLabel(key),
            sortGroups: sortStatusGroups,
        };
    }
    return undefined;
};
